import { parseThemeColors } from '../gameTemplates';

export const sortAndOrderTemplate = (data) => {
  const { title, items, themeColors, themeFonts, backgroundImage } = data;
  const colors = parseThemeColors(themeColors);
  
  return `const InteractiveElement = () => {
  const [currentScreen, setCurrentScreen] = useState('start');
  const [items, setItems] = useState([]);
  const [currentOrder, setCurrentOrder] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const traceId = useRef(null);

  const Confetti = () => {
    useEffect(() => {
      const duration = 3000;
      const end = Date.now() + duration;

      const frame = () => {
        if (typeof confetti !== 'undefined') {
          confetti({
            particleCount: 2,
            angle: 120,
            spread: 55,
            origin: { x: 1 },
            colors: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEEAD']
          });
        }

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };

      frame();
    }, []);

    return null;
  };

  useEffect(() => {
    const items = ${JSON.stringify(items, null, 2)};
    setItems(items);
  }, []);

  const handleBegin = () => {
    try {
      const newTraceId = crypto.randomUUID();
      traceId.current = newTraceId;
      const timestamp = Date.now();
      
      setStartTime(new Date());
      setCurrentScreen('playing');
      
      const shuffled = [...items].sort(() => Math.random() - 0.5);
      setCurrentOrder(shuffled);
      
      if (typeof trackEvent === 'function') {
        trackEvent('interactive_start', newTraceId, {
          startTime: timestamp,
          status: 'started'
        });
      }
      
      if (typeof window !== 'undefined' && window.parent) {
        window.parent.postMessage({
          type: 'interactive_start',
          data: {
            traceId: newTraceId,
            startTime: timestamp,
            status: 'started',
            url: window.location.href
          }
        }, '*');
      }
    } catch (error) {
      console.error('Error starting interactive:', error);
    }
  };

  const handleDragStart = (e, index) => {
    try {
      setDraggedItem(index);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', index.toString());
    } catch (error) {
      console.error('Error in drag start:', error);
    }
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
  };

  const handleDrop = (e, index) => {
    try {
      e.preventDefault();
      const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'));
      
      if (draggedIndex !== index && !isNaN(draggedIndex)) {
        const newOrder = [...currentOrder];
        const draggedContent = newOrder[draggedIndex];
        
        newOrder.splice(draggedIndex, 1);
        newOrder.splice(index, 0, draggedContent);
        
        setCurrentOrder(newOrder);
      }
      
      setDraggedItem(null);
    } catch (error) {
      console.error('Error in drop:', error);
      setDraggedItem(null);
    }
  };

  const handleCheckOrder = () => {
    try {
      setCurrentScreen('summary');
    } catch (error) {
      console.error('Error checking order:', error);
    }
  };

  const handleRestart = () => {
    try {
      setCurrentScreen('start');
      setStartTime(null);
      setDraggedItem(null);
      traceId.current = null;
    } catch (error) {
      console.error('Error restarting:', error);
    }
  };

  const formatTime = (seconds) => {
    try {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return \`\${mins}m \${secs}s\`;
    } catch (error) {
      return '0m 0s';
    }
  };

  if (items.length === 0) {
    return (
      <div className="relative min-h-screen bg-cover bg-center bg-fixed" 
           style={{
        backgroundImage: 'url(${backgroundImage})',
        fontFamily: '${themeFonts}'
      }}>
        <div className="relative z-10 min-h-screen flex items-center justify-center">
          <div className="text-center text-[${colors.primary}]">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-cover bg-center bg-fixed" 
         style={{
        backgroundImage: 'url(https://devprofjim.blob.core.windows.net/usermedia/a1796547-9f54-497e-8499-a9eb6a37436a_ab8a2338-02e5-42ce-9ded-cdf7b48038f5.png)',
        fontFamily: '${themeFonts}'
      }}>
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        {currentScreen === 'start' && (
          <div className="w-full max-w-[807px] relative rounded-2xl outline outline-1 outline-offset-[-1px] outline-[#E0DEEF] bg-[#FFFFFF] backdrop-blur-[20px] overflow-hidden">
            <div className="flex flex-col items-center justify-center gap-10 py-24 px-6">
              <h1 className="text-[${colors.primary}] text-[32px] font-bold leading-[150%] tracking-[-0.022rem] text-center">
                ${title}
              </h1>
              <p className="text-[#4E4E4E] text-[24px] font-medium leading-[150%] tracking-[-0.0165rem] text-center">
                Drag to arrange items in the correct order!
              </p>
              
              <button
                onClick={handleBegin}
                className="w-44 sm:w-[252px] h-12 sm:h-[55px] rounded-md bg-[${colors.primary}] hover:bg-[${colors.primary}90] focus:outline-none flex items-center justify-center transition-all transform hover:scale-105 active:scale-95"
              >
                <span className="text-white text-[24px] font-semibold leading-[150%] tracking-[-0.0165rem]">
                  Begin
                </span>
              </button>
            </div>
          </div>
        )}

        {currentScreen === 'playing' && (
          <div className="w-full max-w-[807px] relative rounded-2xl outline outline-1 outline-offset-[-1px] outline-[#E0DEEF] bg-[#FFFFFF] backdrop-blur-[20px] overflow-hidden">
            <div className="p-10">
              <h2 className="text-[${colors.primary}] text-[32px] font-bold tracking-[-0.022rem] text-center mb-4">
                Arrange in Order
              </h2>
              
              <div className="max-w-xl mx-auto mb-6">
                <div className="bg-[#F8F7FC] rounded-xl p-6">
                  <div className="space-y-3">
                    {currentOrder.map((item, index) => (
                      <div
                        key={item.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDrop={(e) => handleDrop(e, index)}
                        className={\`bg-white border-2 border-[#E0DEEF] p-4 rounded-lg cursor-move transition-all hover:border-[${colors.primary}] hover:shadow-lg transform hover:scale-[1.02] active:scale-95 \${
                          draggedItem === index ? 'opacity-10' : ''
                        }\`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex-shrink-0 w-8 h-8 bg-[#E0DEEF] rounded-full flex items-center justify-center text-[${colors.primary}] font-bold">
                            {index + 1}
                          </div>
                          <p className="text-[#4E4E4E] text-[18px] font-medium select-none"
                            dangerouslySetInnerHTML={{ 
                              __html: DOMPurify.sanitize(item.item) 
                            }}
                          >
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={handleCheckOrder}
                  className="w-56 h-14 rounded-md bg-[${colors.primary}] hover:bg-[${colors.primary}90] text-white text-[20px] font-semibold transition-all transform hover:scale-105 active:scale-95"
                >
                  Check Order
                </button>
              </div>
            </div>
          </div>
        )}

        {currentScreen === 'summary' && (() => {
          try {
            const correctCount = currentOrder.filter((item, index) => 
            parseInt(item.correctOrder) === index + 1
            ).length;
            const scorePercentage = Math.round((correctCount / items.length) * 100);
            const endTime = Date.now();
            const totalTime = startTime ? Math.floor((endTime - startTime.getTime()) / 1000) : 0;

            if (traceId.current) {
              const questionsData = currentOrder.map((item, index) => ({
                id: item.id,
                question: \`Position \${index + 1}: \${item.item}\`,
                correctAnswer: item.correctOrder,
                userAnswer: index + 1,
                isCorrect: parseInt(item.correctOrder) === index + 1,
                explanation: item.explanation
              }));

              if (typeof trackEvent === 'function') {
                trackEvent('interactive_end', traceId.current, {
                  totalScore: items.length,
                  timeTaken: totalTime,
                  userScore: correctCount,
                  endTime: endTime,
                  status: 'completed'
                });
              }

              if (typeof window !== 'undefined' && window.parent) {
                window.parent.postMessage({
                  type: 'interactive_end',
                  data: {
                    traceId: traceId.current,
                    endTime: endTime,
                    timeTaken: totalTime,
                    totalQuestions: items.length,
                    totalScore: items.length,
                    userScore: correctCount,
                    status: 'completed',
                    url: window.location.href,
                    questions: questionsData,
                    summary: {
                      totalCorrect: correctCount,
                      totalIncorrect: items.length - correctCount,
                      accuracy: scorePercentage
                    }
                  }
                }, '*');
              }
              
              traceId.current = null;
            }

            const sortedByCorrect = [...items].sort((a, b) => a.correctOrder - b.correctOrder);

            return (
              <div className="w-full max-w-[1100px] relative rounded-2xl outline outline-1 outline-offset-[-1px] outline-[#E0DEEF] bg-[#FFFFFF] backdrop-blur-[20px] overflow-hidden">
                {scorePercentage === 100 && <Confetti />}
                
                <div className="p-8">
                  <h2 className="text-[${colors.primary}] text-[28px] font-bold tracking-[-0.022rem] text-center mb-4">
                    Your Results
                  </h2>
                  <hr className="w-1/3 mx-auto border-t-[#E0DEEF] mb-4" />

                  <div className="grid grid-cols-3 gap-4 mb-6 max-w-2xl mx-auto">
                    <div className="bg-gradient-to-b from-[#FBFBFF] to-[#ECECF5] p-3 rounded-xl text-center shadow-sm">
                      <div className="text-[#515151] text-xs tracking-wide">Final Score</div>
                      <div className="text-[#2D2544] text-2xl font-bold">{scorePercentage}%</div>
                    </div>
                    <div className="bg-gradient-to-b from-[#FBFBFF] to-[#ECECF5] p-3 rounded-xl text-center shadow-sm">
                      <div className="text-[#515151] text-xs tracking-wide">Total Time</div>
                      <div className="text-[#2D2544] text-2xl font-bold">{formatTime(totalTime)}</div>
                    </div>
                    <div className="bg-gradient-to-b from-[#FBFBFF] to-[#ECECF5] p-3 rounded-xl text-center shadow-sm">
                      <div className="text-[#515151] text-xs tracking-wide">Correct Order</div>
                      <div className="text-[#2D2544] text-2xl font-bold">{correctCount}/{items.length}</div>
                    </div>
                  </div>

                  <Alert className="mb-6 bg-[#FBFBFF] text-center">
                    <AlertDescription>
                      {scorePercentage === 100 
                        ? "Perfect!" 
                        : scorePercentage >= 80 
                        ? "Great job!" 
                        : "Good effort!"}
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-2 mb-6 max-w-5xl mx-auto">
                    {sortedByCorrect.map((correctItem, correctIndex) => {
                      const userPosition = currentOrder.findIndex(item => item.id === correctItem.id) + 1;
                      const isCorrect = userPosition === parseInt(correctItem.correctOrder);
                      
                      return (
                        <div key={correctItem.id} className={\`\${isCorrect ? 'bg-green-50' : 'bg-red-50'} border border-[#E0DEEF] p-3 rounded-xl\`}>
                          <div className="flex items-start gap-3">
                            <span className={\`inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-sm font-bold flex-shrink-0 \${
                              isCorrect ? 'bg-green-500' : 'bg-red-500'
                            }\`}>
                              {isCorrect ? '✓' : '✗'}
                            </span>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[#2D2544] font-semibold">Position {correctItem.correctOrder}:</span>
                                <span className="text-[#4E4E4E] text-base"
                                  dangerouslySetInnerHTML={{ 
                                    __html: DOMPurify.sanitize(correctItem.item) 
                                  }}
                                >
                                </span>
                                {!isCorrect && (
                                  <span className="text-red-600 text-base">(You: position {userPosition})</span>
                                )}
                              </div>
                              <p className="text-[#6E6E7C] italic text-base mt-1"
                                dangerouslySetInnerHTML={{ 
                                  __html: DOMPurify.sanitize(correctItem.explanation) 
                                }}
                              >
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex justify-center">
                    <button
                      onClick={handleRestart}
                      className="w-44 h-12 bg-[${colors.primary}] hover:bg-[${colors.primary}90] rounded-md text-white text-[18px] font-semibold transition-all transform hover:scale-105 active:scale-95"
                    >
                      Restart Activity
                    </button>
                  </div>
                </div>
              </div>
            );
          } catch (error) {
            console.error('Error in summary screen:', error);
            return (
              <div className="w-full max-w-[807px] relative rounded-2xl outline outline-1 outline-offset-[-1px] outline-[#E0DEEF] bg-[#FFFFFF] backdrop-blur-[20px] overflow-hidden">
                <div className="p-8 text-center">
                  <Alert>
                    <AlertDescription>
                      An error occurred while displaying results. Please restart the activity.
                    </AlertDescription>
                  </Alert>
                  <button
                    onClick={handleRestart}
                    className="mt-4 w-44 h-12 bg-[${colors.primary}] hover:bg-[${colors.primary}90] rounded-md text-white text-[18px] font-semibold"
                  >
                    Restart Activity
                  </button>
                </div>
              </div>
            );
          }
        })()}
      </div>
    </div>
  );
}`;
};